import React, { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';

import { useA11ySelector } from '@sg/a11y-helpers';
import { LoadingPlaceholder } from '@sg/garnish';
import {
  getIconForAllergen,
  MenuDietaryPreferences,
  type AllergenOrDietaryRestriction,
} from '@sg/shared-fe/features/menu';
import {
  DietaryPreferencesContent,
  useDietaryPreferencesContent,
} from '@sg/shared-fe/features/menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { AccountScreenContainer, AccountScreenSection, AccountScreenTitle } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useDietaryPreferencesMachine } from '../../context/DietaryPreferences';

export const AccountDietaryRestrictionsScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const containerA11ySelector = useA11ySelector('account.dietary-restrictions.container');

  // ─── Content ───────────────────────────────────────────────────────────────

  const content = useContentfulContentTypeEntry<DietaryPreferencesContent>({
    contentType: 'dietaryPreferences',
  });

  const { title, subtitle, disclaimer, confirmCta, icons } = useDietaryPreferencesContent(
    content?.data?.fields,
  );

  const isLoadingContent = content?.fetching;

  // ─── Machine ───────────────────────────────────────────────────────────────

  const {
    isAllergensEnabled,
    isIdle,
    sortedRestrictionsNames,
    pendingRestrictions,
    isUpdatingRemoteRestrictions,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    confirmChanges,
    toggleRestriction,
    cancelChanges,
    start,
  } = useDietaryPreferencesMachine({ referrer: 'AccountDietaryRestrictions' });

  // ─── Handlers ────────────────────────────────────────────────────────

  const startDietaryPreferencesMachineOnMount = useCallback(() => {
    if (!isLoggedIn) return;
    if (!isIdle) return;

    start({ stayActiveAfterConfirm: true });

    // cancel pending changes on screen blur
    return cancelChanges;
  }, [cancelChanges, isIdle, isLoggedIn, start]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(startDietaryPreferencesMachineOnMount);

  // ─────────────────────────────────────────────────────────────────────

  if (isLoadingContent) {
    return (
      <AccountScreenContainer screen="dietary-restrictions" {...containerA11ySelector.props}>
        <LoadingPlaceholder rows={5} columnWidth={400} rowHeight={60} palette="cream" />
      </AccountScreenContainer>
    );
  }

  return (
    <AccountScreenContainer screen="dietary-restrictions" {...containerA11ySelector.props}>
      <AccountScreenTitle title={title} />

      <AccountScreenSection>
        <MenuDietaryPreferences.Container withoutHorizontalSpacing>
          <MenuDietaryPreferences.Description>{subtitle}</MenuDietaryPreferences.Description>

          {isAllergensEnabled ? (
            <MenuDietaryPreferences.ButtonsContainerV2>
              {sortedRestrictionsNames.map((restriction) => (
                <MenuDietaryPreferences.ButtonV2<AllergenOrDietaryRestriction>
                  icon={getIconForAllergen(restriction, icons)}
                  key={restriction}
                  restriction={restriction}
                  isLoading={isFetchingCustomerPreferences}
                  isChecked={pendingRestrictions[restriction]}
                  isDisabled={isUpdatingRemoteRestrictions}
                  toggleRestriction={toggleRestriction}
                />
              ))}
            </MenuDietaryPreferences.ButtonsContainerV2>
          ) : (
            <MenuDietaryPreferences.ButtonsContainer>
              {sortedRestrictionsNames.map((restriction) => (
                <MenuDietaryPreferences.Button<AllergenOrDietaryRestriction>
                  key={restriction}
                  restriction={restriction}
                  isLoading={isFetchingCustomerPreferences}
                  isChecked={pendingRestrictions[restriction]}
                  isDisabled={isUpdatingRemoteRestrictions}
                  toggleRestriction={toggleRestriction}
                />
              ))}
            </MenuDietaryPreferences.ButtonsContainer>
          )}

          <MenuDietaryPreferences.SubmitButton
            label={confirmCta}
            isLoading={isUpdatingRemoteRestrictions}
            isDisabled={!hasUnsavedPreferences}
            onPress={confirmChanges}
          />

          <MenuDietaryPreferences.Notices>{disclaimer}</MenuDietaryPreferences.Notices>
        </MenuDietaryPreferences.Container>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};
